import { z } from 'zod';

// Domain vocabularies from the spec (docs/reference/Baseline-Feature-doc.pdf). Each one is
// a readonly tuple, a type derived from it, and a zod schema, so services, DTOs and the
// web app all share one definition.

/** Spec §4: the ten alert lifecycle states. */
export const ALERT_STATES = [
  'triaged',
  'draft_ready',
  'awaiting_others',
  'awaiting_verification',
  'needs_human_decision',
  'closed_verified',
  'closed_human',
  'accepted_deviation',
  'superseded',
  'reopened',
] as const;
export type AlertState = (typeof ALERT_STATES)[number];
export const AlertStateSchema = z.enum(ALERT_STATES);

/**
 * States that no longer count as open. FR-002's partial unique index allows at most one
 * alert per line and detection outside this set.
 */
export const CLOSED_ALERT_STATES = [
  'closed_verified',
  'closed_human',
  'accepted_deviation',
  'superseded',
] as const satisfies readonly AlertState[];

export function isOpenAlertState(state: AlertState): boolean {
  return !(CLOSED_ALERT_STATES as readonly AlertState[]).includes(state);
}

/** Spec §3: detections. Presence splits into missing/extra in ERP plus assembly scope. */
export const DETECTIONS = [
  'quantity',
  'configuration',
  'presence_missing_in_erp',
  'presence_extra_in_erp',
  'assembly_not_in_erp',
] as const;
export type Detection = (typeof DETECTIONS)[number];
export const DetectionSchema = z.enum(DETECTIONS);

/** Spec §3: the six diagnoses. */
export const DIAGNOSES = [
  'approved_not_reflected',
  'change_pending',
  'change_rejected',
  'no_change_order',
  'erp_ahead_of_pdm',
  'assembly_not_in_erp',
] as const;
export type Diagnosis = (typeof DIAGNOSES)[number];
export const DiagnosisSchema = z.enum(DIAGNOSES);

/** Severity levels: 0 Low, 1 Medium, 2 High (capped at High, spec §3). */
export const SEVERITIES = [0, 1, 2] as const;
export type Severity = (typeof SEVERITIES)[number];
export const SeveritySchema = z.union([z.literal(0), z.literal(1), z.literal(2)]);
export const MAX_SEVERITY: Severity = 2;

/** Spec §4: action request states. */
export const ACTION_REQUEST_STATES = [
  'open',
  'done',
  'declined',
  'reassigned',
  'withdrawn',
] as const;
export type ActionRequestState = (typeof ACTION_REQUEST_STATES)[number];
export const ActionRequestStateSchema = z.enum(ACTION_REQUEST_STATES);

/** Spec §4: reply classes, per alert. */
export const REPLY_CLASSES = [
  'confirmed_done',
  'disputed',
  'needs_info',
  'unclear',
  'not_addressed',
] as const;
export type ReplyClass = (typeof REPLY_CLASSES)[number];
export const ReplyClassSchema = z.enum(REPLY_CLASSES);

/** Spec §5.3: how a compared BOM line is classified. */
export const DIFF_STATUSES = ['Unchanged', 'Changed', 'Added', 'Removed', 'Moved'] as const;
export type DiffStatus = (typeof DIFF_STATUSES)[number];
export const DiffStatusSchema = z.enum(DIFF_STATUSES);

/**
 * What a detection diff is about: one BOM line, or the whole assembly when the ERP has no
 * record of a released PDM assembly (`assembly_not_in_erp`, ADR 0009).
 */
export const DIFF_SCOPES = ['line', 'assembly'] as const;
export type DiffScope = (typeof DIFF_SCOPES)[number];
export const DiffScopeSchema = z.enum(DIFF_SCOPES);

/**
 * Roles are org-defined at onboarding (spec §2), so a role key is any snake_case string.
 * These are the demo defaults.
 */
export const DEMO_ROLE_KEYS = [
  'design_engineer',
  'supply_owner',
  'change_analyst',
  'manager',
] as const;
export type RoleKey = string;
export const RoleKeySchema = z.string().regex(/^[a-z][a-z0-9_]*$/, 'Use snake_case role keys');

export const RECOMMENDATION_KINDS = ['email', 'request', 'decide', 'watch'] as const;
export type RecommendationKind = (typeof RECOMMENDATION_KINDS)[number];
export const RecommendationKindSchema = z.enum(RECOMMENDATION_KINDS);

/** Visibility grants (spec §2, BR-021). */
export const GRANT_KINDS = [
  'owner',
  'requested',
  'assigned_engineer',
  'delegator',
  'manager_all',
] as const;
export type GrantKind = (typeof GRANT_KINDS)[number];
export const GrantKindSchema = z.enum(GRANT_KINDS);

/** Who wrote an activity-log entry. */
export const ACTOR_TYPES = ['agent', 'user', 'external', 'system'] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];
export const ActorTypeSchema = z.enum(ACTOR_TYPES);

export const DRAFT_STATUSES = ['open', 'sent', 'discarded', 'failed'] as const;
export type DraftStatus = (typeof DRAFT_STATUSES)[number];
export const DraftStatusSchema = z.enum(DRAFT_STATUSES);

export const OUTBOUND_MESSAGE_STATUSES = ['queued', 'sent', 'failed'] as const;
export type OutboundMessageStatus = (typeof OUTBOUND_MESSAGE_STATUSES)[number];
export const OutboundMessageStatusSchema = z.enum(OUTBOUND_MESSAGE_STATUSES);

export const THREAD_ORIGINS = ['originated', 'owner_linked'] as const;
export type ThreadOrigin = (typeof THREAD_ORIGINS)[number];
export const ThreadOriginSchema = z.enum(THREAD_ORIGINS);

/** Spec §5.8: demo connectors. */
export const CONNECTOR_KINDS = [
  'pdm',
  'erp',
  'change_orders',
  'purchase_orders',
  'gmail',
  'notifications',
] as const;
export type ConnectorKind = (typeof CONNECTOR_KINDS)[number];
export const ConnectorKindSchema = z.enum(CONNECTOR_KINDS);

export const CONNECTOR_STATUSES = ['connected', 'failed', 'not_configured'] as const;
export type ConnectorStatus = (typeof CONNECTOR_STATUSES)[number];
export const ConnectorStatusSchema = z.enum(CONNECTOR_STATUSES);

export const SOURCE_SYSTEMS = ['PDM', 'ERP'] as const;
export type SourceSystem = (typeof SOURCE_SYSTEMS)[number];
export const SourceSystemSchema = z.enum(SOURCE_SYSTEMS);

export const ASSEMBLY_LIFECYCLES = ['released', 'production'] as const;
export type AssemblyLifecycle = (typeof ASSEMBLY_LIFECYCLES)[number];
export const AssemblyLifecycleSchema = z.enum(ASSEMBLY_LIFECYCLES);

export const CHANGE_ORDER_STATUSES = ['approved', 'in_review', 'rejected', 'cancelled'] as const;
export type ChangeOrderStatus = (typeof CHANGE_ORDER_STATUSES)[number];
export const ChangeOrderStatusSchema = z.enum(CHANGE_ORDER_STATUSES);

export const PURCHASE_ORDER_STATUSES = ['open', 'planned'] as const;
export type PurchaseOrderStatus = (typeof PURCHASE_ORDER_STATUSES)[number];
export const PurchaseOrderStatusSchema = z.enum(PURCHASE_ORDER_STATUSES);

/** Spec §5.1: what started a detection run. */
export const COMPARE_TRIGGERS = ['release', 'watch', 'recheck'] as const;
export type CompareTrigger = (typeof COMPARE_TRIGGERS)[number];
export const CompareTriggerSchema = z.enum(COMPARE_TRIGGERS);

export const COMPARE_RUN_STATUSES = ['running', 'succeeded', 'failed'] as const;
export type CompareRunStatus = (typeof COMPARE_RUN_STATUSES)[number];
export const CompareRunStatusSchema = z.enum(COMPARE_RUN_STATUSES);

/** Manual compare views on the Compare page. */
export const COMPARE_VIEWS = ['engineering', 'purchasing'] as const;
export type CompareView = (typeof COMPARE_VIEWS)[number];
export const CompareViewSchema = z.enum(COMPARE_VIEWS);

/** The Alerts list "View" select (plan Appendix B). */
export const ALERT_VIEWS = ['all', 'action', 'waiting', 'closed'] as const;
export type AlertView = (typeof ALERT_VIEWS)[number];
export const AlertViewSchema = z.enum(ALERT_VIEWS);

/** `POST /alerts/:id/actions/<action>` (plan Phase 7.4). */
export const ALERT_ACTIONS = [
  'accept-recommendation',
  'mark-fixed',
  'accept-deviation',
  'reassign',
  'recheck',
  'close',
  'escalate',
] as const;
export type AlertAction = (typeof ALERT_ACTIONS)[number];
export const AlertActionSchema = z.enum(ALERT_ACTIONS);

/** `POST /requests/:id/<action>`. No bulk variants (spec §4). */
export const REQUEST_ACTIONS = ['done', 'decline', 'reassign'] as const;
export type RequestAction = (typeof REQUEST_ACTIONS)[number];
export const RequestActionSchema = z.enum(REQUEST_ACTIONS);

export const SSO_PROVIDERS = ['microsoft', 'google', 'okta'] as const;
export type SsoProvider = (typeof SSO_PROVIDERS)[number];
export const SsoProviderSchema = z.enum(SSO_PROVIDERS);
